import { useEffect } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import dayjs from 'dayjs'

import { useTimerStore } from 'stores'
import { useGoogleAppdata } from 'hooks'

import {
  Main,
  TextField,
  ColorField,
  Button,
  Heading,
  SwitchField,
  SelectField,
} from 'components'

import {
  Form,
  Columns,
  Buttons,
} from './editStyle'

const defaultValues = {
  name: '',
  color: 'blue_gray',
  date: '',
  time: '00:00',
  isRepeating: false,
  repeatFrequency: 'year',
}

const Edit = () => {
  const addTimer = useTimerStore(state => state.addTimer)
  const editTimer = useTimerStore(state => state.editTimer)
  const timers = useTimerStore(state => state.timers)
  const navigate = useNavigate()
  const { id } = useParams()
  const { save } = useGoogleAppdata()

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    formState: { isDirty, errors },
  } = useForm({ defaultValues })

  const watchDate = watch('date')
  const watchRepeating = watch('isRepeating')

  const onSubmit = values => {
    if (id) {
      editTimer(parseInt(id), {
        name: values.name,
        color: values.color,
        date: `${values.date}T${values.time}`,
        isRepeating: values.isRepeating,
        repeatFrequency: values.repeatFrequency,
      })
      save()
      navigate(`/timer/${id}`)
    } else {
      const newId = addTimer({
        name: values.name,
        color: values.color,
        date: `${values.date}T${values.time}`,
        isRepeating: values.isRepeating,
        repeatFrequency: values.repeatFrequency,
      })
      save()
      navigate(`/timer/${newId}`)
    }
  }

  useEffect(() => {
    if (id) {
      const timer = timers.find(t => t.id === parseInt(id))
      reset({
        name: timer.name,
        color: timer.color,
        date: dayjs(timer.date).format('YYYY-MM-DD'),
        time: dayjs(timer.date).format('HH:mm'),
        isRepeating: timer.isRepeating,
        repeatFrequency: timer.repeatFrequency,
      })
    }
  }, [id, reset, timers])
  
  return (
    <Main center>
      <Heading>{id ? `Edit ${timers.find(t => t.id === parseInt(id)).name}` : 'Create a new countdown'}</Heading>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <TextField
          {...register('name', {
            required: 'Your countdown needs a name'
          })}
          label="Name"
          placeholder="My Birthday"
          autocapitalize="words"
          autoFocus
          required
          error={errors.name?.message}
        />
        <Controller
          control={control}
          name="color"
          render={({
            field: { onChange, value, name }
          }) => (
            <ColorField
              label="Color"
              name={name}
              onChange={onChange}
              value={value}
              colors={{
                'red': 'Red',
                'pink': 'Pink',
                'purple': 'Purple',
                'deep_purple': 'Deep purple',
                'indigo': 'Indigo',
                'blue': 'Blue',
                'light_blue': 'Light blue',
                'cyan': 'Cyan',
                'teal': 'Teal',
                'green': 'Green',
                'light_green': 'Light green',
                'lime': 'Lime',
                'yellow': 'Yellow',
                'amber': 'Amber',
                'orange': 'Orange',
                'deep_orange': 'Deep orange',
                'brown': 'Brown',
                'blue_gray': 'Blue gray',
              }}
              required
              error={errors.color?.message}
            />
          )}
        />
        <Columns>
          <TextField
            {...register('date', {
              required: 'You need a date',
              validate: value => dayjs(value).isAfter(dayjs().subtract(1, 'day')) || 'The date can\'t be in the past',
            })}
            type="date"
            label="Date"
            required
            error={errors.date?.message}
          />
          <TextField
            {...register('time', {
              required: 'You need a time',
              validate: value => dayjs(`${watchDate}T${value}`).isAfter(dayjs()) || 'The time can\'t be in the past',
            })}
            type="time"
            label="Time"
            required
            error={errors.time?.message}
          />
        </Columns>

        <Controller
          control={control}
          name="isRepeating"
          render={({
            field: { onChange, value, name }
          }) => (
            <SwitchField
              label="Repeating countdown"
              value={value}
              onChange={onChange}
              name={name}
              id="repeating_countdown"
            />
          )}
        />

        {watchRepeating && (
          <Controller
            control={control}
            name="repeatFrequency"
            rules={{
              validate: value => !watchRepeating || !!value || 'Frequency is required for a repeating countdown',
            }}
            render={({
              field: { onChange, value, name }
            }) => (
              <SelectField
                label="Repeat frequency"
                value={value}
                onChange={onChange}
                name={name}
                id="repeating_frequency"
                options={{
                  day: 'Daily',
                  week: 'Weekly',
                  month: 'Monthly',
                  year: 'Yearly',
                }}
                error={errors.repeatFrequency?.message}
              />
            )}
          />
        )}

        <Buttons>
          <Button to={id ? `/timer/${id}` : '/'} as={Link} secondary>Cancel</Button>
          <Button type="submit" disabled={!isDirty}>{id ? 'Save' : 'Create'}</Button>
        </Buttons>
      </Form>
    </Main>
  )
}

export default Edit